import React, { useState, useEffect } from 'react';
import {
  AiOutlineCheckCircle,
  AiOutlineCloseCircle
} from 'react-icons/ai';
import { createInquiry } from '../../api/action/inquiry';
import { useSelector } from 'react-redux';
import 'react-phone-input-2/lib/style.css';
import validator from 'validator';
import { buildInquiryNotificationFields } from '../../template/TemplateContactUtils.jsx';

// Import domain images
import { FLEX_DOMAIN } from "../s3-assets/FlexOffice";
import { ASSESSMENT } from "../s3-assets/Assessment";
import { EDUCATION } from "../s3-assets/Education";
import { RETAIL } from "../s3-assets/Retail";

// Domain → Config
export const DOMAIN_MAP = {
  FLEX_OFFICE: FLEX_DOMAIN,
  EDUCATION: EDUCATION,
  ASSESSMENT: ASSESSMENT,
  RETAIL: RETAIL,
};

// Domain → Which image key to use
export const DOMAIN_IMAGE_KEY = {
  FLEX_OFFICE: "IMAGE_4",
  EDUCATION: "IMAGE_1",
  ASSESSMENT: "IMAGE_1",
  RETAIL: "IMAGE_5",
};

// Domain → Text content (heading + description + buttonColor)
export const DOMAIN_TEXT = {
  FLEX_OFFICE: {
    heading: <>Get in <span className="text-blue-600">touch</span></>,
    description: "Have questions about premium workspaces? We're here to assist you.",
    buttonColor: "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
  },
  ASSESSMENT: {
    heading: <>Start your <span className="text-[#746EAC]">assessment</span></>,
    description: "Need support with exams or learning? Our team is ready to help.",
    buttonColor: "bg-[#746EAC] hover:bg-[#5e5891]"
  },
  RETAIL: {
    heading: (companyName) => <>Connect with <span className="text-[#f4a649]">{companyName}</span></>,
    description: "We’d love to hear your thoughts and inquiries about our products.",
    buttonColor: "bg-[#f4a649] hover:bg-[#d98a2f]"
  },
  EDUCATION: {
    heading: <>Get in <span className="education-primary-text">touch</span></>,
    description: "Have questions about admissions or courses? We're here to guide you.",
    buttonColor: "education-primary-button"
  }
};

//  Default fallback for unknown domain types
export const DEFAULT_CONTACT_CONFIG = {
  IMAGE: RETAIL.IMAGES.IMAGE_5,
  heading: <>Get in <span className="text-blue-600">touch</span></>,
  description: "Have questions or need support? We're here to help you.",
  buttonColor: "bg-blue-600 hover:bg-blue-700",
};

const ContactUsSection = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    query: '',
    contactPersonList: []
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    query: ''
  });

  const [alert, setAlert] = useState({
    message: "",
    type: "", // 'success' or 'error'
    show: false
  });

  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  const company = useSelector((state) => state.companyReducer.company);

  // ----- Phone Number Handler -----
  const handlePhoneChange = (value) => {
    let digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length > 10) {
      digitsOnly = digitsOnly.slice(0, 10);
    }
    setFormData({ ...formData, phoneNumber: digitsOnly });
    setErrors({ ...errors, phoneNumber: '' });
  };

  // ----- Input Handlers -----
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleFocus = (fieldName) => setFocusedField(fieldName);
  const handleBlur = () => setFocusedField('');

  // ----- Validation -----
  const validateForm = () => {
    let valid = true;
    const newErrors = { firstName: '', lastName: '', email: '', phoneNumber: '', query: '' };

    if (!formData.firstName.trim() || formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
      valid = false;
    }

    if (!formData.lastName.trim() || formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
      valid = false;
    }

    if (!formData.email.trim() || !validator.isEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      valid = false;
    }

    if (!formData.phoneNumber || formData.phoneNumber.replace(/\D/g, '').length !== 10) {
      newErrors.phoneNumber = 'Please enter a 10-digit phone number';
      valid = false;
    }

    if (!formData.query.trim()) {
      newErrors.query = 'Message is required';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  // ----- Submit -----
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    const companyOperatingLocation = company?.operatingLocation?.code;
    let countryCode = "";
    if (companyOperatingLocation === "IND") countryCode = "+91";
    else if (companyOperatingLocation === "US") countryCode = "+1";

    const inquiryData = {
      user: {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber,
        countryCode: countryCode,
        contactPersonList: []
      },
      query: formData.query.trim(),
      company: {
        primaryId: company?.primaryId,
        name: company?.name,
        url: company?.url,
        domain: { type: company?.domain?.type || "INSTITUTION" }
      },
      ...buildInquiryNotificationFields(company)
    };

    try {
      const result = await createInquiry(inquiryData);
      localStorage.setItem("inquiryData", JSON.stringify(result));
      setAlert({ message: "Thanks for reaching out! Your message has been received — we’ll be in touch soon.", type: "success", show: true });
      setFormData({ firstName: '', lastName: '', email: '', phoneNumber: '', query: '', contactPersonList: [] });
    } catch {
      setAlert({ message: "Error sending enquiry. Please check your connection and try again.", type: "error", show: true });
    } finally {
      setLoading(false);
    }
  };

  // ----- Auto hide alert -----
  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert({ ...alert, show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // ----- Field Styles -----
  const getFieldClasses = (fieldName, hasError) => {
    const base = "w-full px-12 py-3 border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2";
    const focused = focusedField === fieldName ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200" : "";
    const error = hasError ? "border-red-500 bg-red-50" : "border-gray-300 hover:border-gray-400";
    const success = !hasError && formData[fieldName] ? "border-emerald-400 bg-emerald-50" : "";
    return `${base} ${focused || error || success}`;
  };

  const companyOperatingLocation = company?.operatingLocation?.code;
  let countryCode = "";
  if (companyOperatingLocation === "IND") countryCode = "+91";
  else if (companyOperatingLocation === "US") countryCode = "+1";

  const domainType = company?.domain?.type || "UNKNOWN";

  //  Safe fallback for image + text
  const contactImage =
    DOMAIN_MAP[domainType]?.IMAGES?.[DOMAIN_IMAGE_KEY[domainType]] ||
    DEFAULT_CONTACT_CONFIG.IMAGE;

  const { heading: headingConfig, description, buttonColor } =
    DOMAIN_TEXT[domainType] || DEFAULT_CONTACT_CONFIG;
  const heading = typeof headingConfig === 'function'
    ? headingConfig(company?.name)
    : headingConfig;

  return (
    <div
      id="contact-section"
      className="bg-white py-12 px-4 sm:px-6 lg:px-8"
      style={{ paddingTop: '6rem' }}
    >
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <h1 id="contact-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {heading}
          </h1>
          <p id="contact-description" className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            {description}
          </p>
        </div>

        {/* Grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">

          {/* Image */}
          <div className="lg:order-1 relative h-full min-h-[400px] sm:min-h-[500px] rounded-xl overflow-hidden shadow-lg">
            <img
              src={contactImage}
              alt="Contact Us - Professional Team"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <h3 id="contact-image-heading" className="text-2xl font-bold mb-2">Let's Connect</h3>
              <p className="text-blue-200">Building relationships that matter</p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:order-2 bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-6 sm:p-8 md:p-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 text-center">
                Send us a Message
              </h2>

              {alert.show && (
                <div className={`mb-6 p-4 rounded-lg flex items-start ${alert.type === 'success' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                  {alert.type === 'success' ? (
                    <AiOutlineCheckCircle className="flex-shrink-0 h-5 w-5 mt-0.5 mr-3" />
                  ) : (
                    <AiOutlineCloseCircle className="flex-shrink-0 h-5 w-5 mt-0.5 mr-3" />
                  )}
                  <span className="text-sm sm:text-base">{alert.message}</span>
                </div>
              )}

              <form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
                {/* First + Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="input-firstName" className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      onFocus={() => handleFocus("firstName")}
                      onBlur={handleBlur}
                      className={getFieldClasses("firstName", errors.firstName)}
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AiOutlineCloseCircle className="mr-1" />{errors.firstName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="input-lastName" className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      onFocus={() => handleFocus("lastName")}
                      onBlur={handleBlur}
                      className={getFieldClasses("lastName", errors.lastName)}
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AiOutlineCloseCircle className="mr-1" />{errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="input-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => handleFocus('email')}
                    onBlur={handleBlur}
                    className={getFieldClasses('email', errors.email)}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AiOutlineCloseCircle className="mr-1" />{errors.email}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="input-phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    {countryCode && (
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 font-medium z-10">
                        {countryCode}
                      </span>
                    )}
                    <input
                      id="phoneNumber"
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      onFocus={() => handleFocus('phoneNumber')}
                      onBlur={handleBlur}
                      className={`${getFieldClasses('phoneNumber', errors.phoneNumber)} ${countryCode ? 'pl-16' : 'pl-3'}`}
                      placeholder={countryCode ? "1234567890" : "Enter phone number"}
                      maxLength="10"
                    />
                  </div>
                  {errors.phoneNumber && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AiOutlineCloseCircle className="mr-1" />{errors.phoneNumber}
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="input-message" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="query"
                    rows="5"
                    value={formData.query}
                    onChange={handleChange}
                    onFocus={() => handleFocus('query')}
                    onBlur={handleBlur}
                    className={`${getFieldClasses('query', errors.query)} !pt-3`}
                    placeholder="How can we help you?"
                  ></textarea>
                  {errors.query && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AiOutlineCloseCircle className="mr-1" />{errors.query}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <div className="pt-2">
                  <button
                    id="submit"
                    type="submit"
                    disabled={loading}
                    className={`w-full py-3 px-6 text-white font-medium rounded-lg shadow-md transition-all duration-200 flex items-center justify-center ${buttonColor} ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 ml-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.897 28.897 0 003.105 2.289z" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <p className="text-sm text-gray-500">
                  Need immediate assistance?{' '}
                  <span className="text-blue-600 font-medium">We typically respond within 24 to 48 hours</span>
                </p>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUsSection;
